import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, Shield, KeyRound, X, Trash2 } from "lucide-react";
import { PageHeader, FormRow } from "@/pages/Accounts";

export default function UsersPage() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    role: "analyst",
  });
  const [busy, setBusy] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [scopesOpen, setScopesOpen] = useState(false);
  const [scopeUser, setScopeUser] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ email: "", password: "", name: "", role: "analyst" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const resetPw = async () => {
    if (!pwUser) return;
    setBusy(true);
    try {
      await api.post(`/users/${pwUser.id}/reset-password`, { new_password: pwValue });
      toast.success("Password reset. User must change on next login.");
      setPwOpen(false);
      setPwValue("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const softDelete = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Deactivated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Users"
        description="Admins and analysts. Admins can create both. Analysts see only what they are scoped to."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-user-button">
                <Plus className="mr-2 h-4 w-4" />
                New user
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="new-user-dialog">
              <DialogHeader>
                <DialogTitle>Create user</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <FormRow label="Email">
                  <Input
                    data-testid="user-email-input"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </FormRow>
                <FormRow label="Name">
                  <Input
                    data-testid="user-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </FormRow>
                <FormRow label="Password (initial)">
                  <Input
                    type="password"
                    data-testid="user-password-input"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                </FormRow>
                <FormRow label="Role">
                  <Select
                    value={form.role}
                    onValueChange={(v) => setForm({ ...form, role: v })}
                  >
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">admin</SelectItem>
                      <SelectItem value="analyst">analyst</SelectItem>
                    </SelectContent>
                  </Select>
                </FormRow>
              </div>
              <DialogFooter>
                <Button
                  onClick={submit}
                  disabled={busy || !form.email || !form.password || !form.name}
                  data-testid="user-create-submit"
                >
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Card data-testid="users-card">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Active</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items === null && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <Loader2 className="inline h-4 w-4 animate-spin" />
                  </TableCell>
                </TableRow>
              )}
              {items?.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="text-xs">{u.email}</TableCell>
                  <TableCell className="text-xs">{u.name}</TableCell>
                  <TableCell>
                    <Badge variant={u.role === "admin" ? "default" : "secondary"}>
                      {u.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={u.is_active ? "default" : "outline"}>
                      {u.is_active ? "active" : "inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setScopeUser(u);
                        setScopesOpen(true);
                      }}
                      data-testid={`user-scopes-${u.email}`}
                    >
                      <Shield className="mr-1 h-3 w-3" />
                      Scopes
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setPwUser(u);
                        setPwOpen(true);
                      }}
                      data-testid={`user-reset-pw-${u.email}`}
                    >
                      <KeyRound className="mr-1 h-3 w-3" />
                      Reset PW
                    </Button>
                    {u.is_active && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => softDelete(u)}
                        data-testid={`user-delete-${u.email}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent data-testid="reset-pw-dialog">
          <DialogHeader>
            <DialogTitle>Reset password — {pwUser?.email}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <FormRow label="New password (min 8 chars)">
              <Input
                type="password"
                data-testid="reset-pw-input"
                value={pwValue}
                onChange={(e) => setPwValue(e.target.value)}
              />
            </FormRow>
          </div>
          <DialogFooter>
            <Button
              onClick={resetPw}
              disabled={busy || pwValue.length < 8}
              data-testid="reset-pw-submit"
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ScopesDialog
        open={scopesOpen}
        onOpenChange={setScopesOpen}
        user={scopeUser}
      />
    </div>
  );
}

function ScopesDialog({ open, onOpenChange, user }) {
  const [scopes, setScopes] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [type, setType] = useState("project");
  const [id, setId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !open) return;
    const load = async () => {
      try {
        const [s, a, p] = await Promise.all([
          api.get(`/users/${user.id}/scopes`),
          api.get("/accounts"),
          api.get("/projects"),
        ]);
        setScopes(s.data);
        setAccounts(a.data);
        setProjects(p.data);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    };
    load();
  }, [user, open]);

  const grant = async () => {
    if (!type) return;
    setBusy(true);
    try {
      await api.post(`/users/${user.id}/scopes`, {
        scope_type: type,
        scope_id: type === "global" ? null : id,
      });
      const s = await api.get(`/users/${user.id}/scopes`);
      setScopes(s.data);
      setId("");
      toast.success("Scope granted");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (row) => {
    try {
      await api.delete(`/users/${user.id}/scopes/${row.id}`);
      const s = await api.get(`/users/${user.id}/scopes`);
      setScopes(s.data);
      toast.success("Scope revoked");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="scopes-dialog">
        <DialogHeader>
          <DialogTitle>Scopes — {user?.email}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="border rounded-md p-3 space-y-2">
            <div className="text-xs font-medium">Current scopes</div>
            {scopes.length === 0 && (
              <div className="text-xs text-muted-foreground">None (this user has no access)</div>
            )}
            <div className="flex flex-wrap gap-2">
              {scopes.map((s) => (
                <Badge
                  key={s.id}
                  variant="outline"
                  className="text-[10px] py-0.5"
                  data-testid={`scope-badge-${s.id}`}
                >
                  {s.scope_type}
                  {s.scope_id ? `: ${s.scope_id.slice(-6)}` : ""}
                  <button
                    onClick={() => revoke(s)}
                    className="ml-1"
                    data-testid={`scope-revoke-${s.id}`}
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
          <div className="border rounded-md p-3 space-y-2">
            <div className="text-xs font-medium">Grant new scope</div>
            <div className="flex gap-2">
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="max-w-[120px]" data-testid="scope-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">global</SelectItem>
                  <SelectItem value="account">account</SelectItem>
                  <SelectItem value="project">project</SelectItem>
                </SelectContent>
              </Select>
              {type !== "global" && (
                <Select value={id} onValueChange={setId}>
                  <SelectTrigger data-testid="scope-target-select">
                    <SelectValue placeholder={type === "account" ? "select account" : "select project"} />
                  </SelectTrigger>
                  <SelectContent>
                    {(type === "account" ? accounts : projects).map((x) => (
                      <SelectItem key={x.id} value={x.id}>
                        {x.name || x.slug}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                size="sm"
                onClick={grant}
                disabled={busy || (type !== "global" && !id)}
                data-testid="scope-grant-btn"
              >
                Grant
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
